import * as yup from "yup";
import { CountryCode, Examples, getExampleNumber } from "libphonenumber-js";
import examples from "libphonenumber-js/examples.mobile.json";

export interface CountryCodeOption {
    value: string;
    label: string;
}

export const COUNTRY_CODE_OPTIONS: CountryCodeOption[] = [
    { value: "+91", label: "IN +91" },
    { value: "+1", label: "US +1" },
    { value: "+44", label: "GB +44" },
    { value: "+971", label: "AE +971" },
    { value: "+966", label: "SA +966" },
    { value: "+61", label: "AU +61" },
];

export const DEFAULT_COUNTRY_CODE = "+91";

export const getIsoCode = (dialCode?: string): CountryCode | undefined => {
    const option = COUNTRY_CODE_OPTIONS.find((item) => item.value === dialCode);
    return option ? (option.label.split(" ")[0] as CountryCode) : undefined;
};

const GENERIC_LENGTH_MESSAGE = "Mobile number length is incorrect for the selected region.";

export const getExpectedDigitLength = (isoCode?: CountryCode): number | null => {
    if (!isoCode) return null;

    const exampleNumber = getExampleNumber(isoCode, examples as Examples);
    const length = exampleNumber?.nationalNumber?.length ?? 0;

    return length > 0 ? length : null;
};

const getLengthErrorMessage = (isoCode?: CountryCode): string => {
    const expectedLength = getExpectedDigitLength(isoCode);

    return expectedLength
        ? `Mobile number must be exactly ${expectedLength} digits for this region.`
        : GENERIC_LENGTH_MESSAGE;
};

export const validatePhoneForCountry = (
    value: string | null | undefined,
    dialCode?: string
): true | string => {
    const digits = String(value ?? "").replace(/\D/g, "");
    if (!digits) return "Mobile Number is Required";

    const isoCode = getIsoCode(dialCode);
    if (!isoCode) return "Invalid country code.";

    const expectedLength = getExpectedDigitLength(isoCode);
    if (!expectedLength) return GENERIC_LENGTH_MESSAGE;

    return digits.length === expectedLength ? true : getLengthErrorMessage(isoCode);
};

export const phoneNumberSchema = (
    countryFieldName: string,
    options?: { requiredMessage?: string }
) => {
    const base = yup.string().trim();
    const schema = options?.requiredMessage ? base.required(options.requiredMessage) : base;

    return schema.test("phone-by-country", function (value) {
        if (!value) return true;

        const result = validatePhoneForCountry(value, this.parent?.[countryFieldName]);
        return result === true ? true : this.createError({ message: result });
    });
};

export const joinPhoneNumber = (dialCode?: string, nationalNumber?: string | null): string => {
    const digits = String(nationalNumber ?? "").replace(/\D/g, "");
    if (!digits) return "";

    return `${dialCode || DEFAULT_COUNTRY_CODE}${digits}`;
};

const getDialCodeDigits = (option: CountryCodeOption) => option.value.slice(1);

const findLeadingDialCode = (
    digits: string,
    requireExactNationalLength: boolean
): { countryCode: string; nationalNumber: string } | null => {
    const matched = [...COUNTRY_CODE_OPTIONS]
        .sort((a, b) => b.value.length - a.value.length)
        .find((option) => {
            const codeDigits = getDialCodeDigits(option);
            if (!digits.startsWith(codeDigits)) return false;

            const remainder = digits.slice(codeDigits.length);
            if (!remainder) return false;
            if (!requireExactNationalLength) return true;

            return remainder.length === getExpectedDigitLength(getIsoCode(option.value));
        });

    if (!matched) return null;

    return {
        countryCode: matched.value,
        nationalNumber: digits.slice(getDialCodeDigits(matched).length),
    };
};

export const splitPhoneNumber = (
    storedValue?: string | number | null
): { countryCode: string; nationalNumber: string } => {
    const raw = String(storedValue ?? "").trim();
    const digits = raw.replace(/\D/g, "");
    if (!digits) return { countryCode: DEFAULT_COUNTRY_CODE, nationalNumber: "" };

    const matched = findLeadingDialCode(digits, !raw.startsWith("+"));

    return matched ?? { countryCode: DEFAULT_COUNTRY_CODE, nationalNumber: digits };
};